import { useState, useEffect } from 'react';
import { toZonedTime } from 'date-fns-tz';
import { differenceInSeconds, endOfMonth, format, isValid, parse } from 'date-fns';
import Papa from 'papaparse';

import { clearAuth } from '../store/slices/authSlice';
import { clearCommonSlice } from '../store/slices/commonSlice';
import { clearCallingSlice } from '../store/slices/callingSlice';
import { clearDialSlice } from '../store/slices/dialSlice';
import { clearDashboardSlice } from '../store/slices/dashboardSlice';
import { clearRealtimeReportSlice } from '../store/slices/tenantSlices/realtimeReportSlice';
import { clearDailyReportSlice } from '../store/slices/tenantSlices/dailyReportSlice';
import { clearCallHistorySlice } from '../store/slices/callHistorySlice';
import { clearAgentPerformanceSlice } from '../store/slices/tenantSlices/agentPerformanceSlice';
import { clearConfigurationSlice } from '../store/slices/tenantSlices/configurationSlice';
import { clearCompanySlice } from '../store/slices/adminSlices/companySlice';
import { clearVoiceBroadcastBillingSlice } from '../store/slices/voiceBroadcastBillingSlice';
import { clearIncomingSlice } from '../store/slices/tenantSlices/incomingSlice';
import { clearTenantDashboardSlice } from '../store/slices/tenantSlices/tenantDashboardSlice';
import { clearProductSlice } from '../store/slices/tenantSlices/productSlice';
import { clearModulePermissionSlice } from '../store/slices/adminSlices/modulePermissionSlice';
import { clearCompanyRoleSlice } from '../store/slices/adminSlices/companyRoleSlice';
import { clearSupervisorSlice } from '../store/slices/tenantSlices/supervisorSlice';
import { clearAdminDashboardSlice } from '../store/slices/adminSlices/adminDashboardSlice';
import { clearAuditorSlice } from '../store/slices/tenantSlices/auditorSlice';
import { clearChatSlice } from '../store/slices/tenantSlices/chatSlice';
import { clearAuditorDashboardSlice } from '../store/slices/tenantSlices/auditorDashboardSlice';
import { clearTimezoneSlice } from '../store/slices/adminSlices/timezoneSlice';
import { clearBrandingSlice } from '../store/slices/adminSlices/brandingSlice';
import { clearMetaHelpDeskSlice } from '../store/slices/tenantSlices/metaHelpDeskSlice';
import { setBreakPending } from './breakState';

export const clearPersister = (dispatch) => {
  // The break latch lives outside Redux (see breakState.js) so it survives a
  // `clearDialSlice()`. Logout is a SPA navigation, not a reload — without this a
  // logout taken while on break would leave the next session permanently latched
  // and deaf to call events, with no break showing that could be ended.
  setBreakPending(false);
  dispatch(clearAuth());
  dispatch(clearCommonSlice());
  dispatch(clearDialSlice());
  dispatch(clearCallingSlice());
  dispatch(clearDashboardSlice());
  dispatch(clearRealtimeReportSlice());
  dispatch(clearDailyReportSlice());
  dispatch(clearCallHistorySlice());
  dispatch(clearAgentPerformanceSlice());
  dispatch(clearConfigurationSlice());
  dispatch(clearIncomingSlice());
  dispatch(clearTenantDashboardSlice());
  dispatch(clearProductSlice());
  dispatch(clearCompanySlice());
  dispatch(clearVoiceBroadcastBillingSlice());
  dispatch(clearModulePermissionSlice());
  dispatch(clearCompanyRoleSlice());
  dispatch(clearSupervisorSlice());
  dispatch(clearAuditorSlice());
  dispatch(clearAdminDashboardSlice());
  dispatch(clearChatSlice());
  dispatch(clearAuditorDashboardSlice());
  dispatch(clearTimezoneSlice());
  dispatch(clearBrandingSlice());
  dispatch(clearMetaHelpDeskSlice());
};

export const getFullDomain = () => {
  try {
    const url = window.location.href;
    const domain = new URL(url).hostname;
    return domain;
  } catch (error) {
    console.error('Invalid URL:', error);
    return null;
  }
};

export const getCurrentDateTime = (timezone) => {
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      const formattedDate = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: timezone
      }).format(new Date());

      setCurrentTime(formattedDate);
    }, 1000);

    return () => clearInterval(interval);
  }, [timezone]);

  return currentTime;
};

export const fileDownload = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.download;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const refreshTimeOptions = [
  { label: '2 second', value: 2 },
  { label: '5 second', value: 5 },
  { label: '10 second', value: 10 },
  { label: '15 second', value: 15 },
  { label: '30 second', value: 30 },
  { label: '1 minute', value: 60 },
  { label: '2 minute', value: 120 }
];

export const calculateLoginDuration = (loginTime, logoutTime) => {
  const parsedLoginTime = parse(loginTime, 'yyyy-MM-dd HH:mm:ss', new Date());
  const parsedLogoutTime = parse(logoutTime, 'yyyy-MM-dd HH:mm:ss', new Date());

  const durationSec = differenceInSeconds(parsedLogoutTime, parsedLoginTime);

  const hours = Math.floor(durationSec / 3600);
  const minutes = Math.floor((durationSec % 3600) / 60);
  const seconds = Math.floor(durationSec % 60);

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

export const calculateDurations = (reportData = []) => {
  let totalDurationSec = 0;

  const durations = reportData.map((val) => {
    let totalWorkTimeSec = 0;

    if (val?.login && val?.logout) {
      const loginTime = parse(val?.login, 'yyyy-MM-dd HH:mm:ss', new Date());
      const logoutTime = parse(val?.logout, 'yyyy-MM-dd HH:mm:ss', new Date());
      totalWorkTimeSec = differenceInSeconds(logoutTime, loginTime);
    }

    if (val?.break_in && val?.break_out) {
      const breakInTime = parse(val?.break_in, 'yyyy-MM-dd HH:mm:ss', new Date());
      const breakOutTime = parse(val?.break_out, 'yyyy-MM-dd HH:mm:ss', new Date());
      totalWorkTimeSec = differenceInSeconds(breakOutTime, breakInTime);
    }

    const hours = Math.floor(totalWorkTimeSec / 3600);
    const minutes = Math.floor((totalWorkTimeSec % 3600) / 60);
    const seconds = Math.floor(totalWorkTimeSec % 60);

    const formattedDuration = [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':');

    totalDurationSec += totalWorkTimeSec;

    return { formattedDuration, breakName: val?.break_name || '-' };
  });

  const totalHours = Math.floor(totalDurationSec / 3600);
  const totalMinutes = Math.floor((totalDurationSec % 3600) / 60);
  const totalSeconds = Math.floor(totalDurationSec % 60);

  const totalFormattedDuration = [
    totalHours.toString().padStart(2, '0'),
    totalMinutes.toString().padStart(2, '0'),
    totalSeconds.toString().padStart(2, '0')
  ].join(':');

  return { durations, totalDuration: totalFormattedDuration };
};

export const trueFalseOptions = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' }
];

export const onOffOptions = [
  { label: 'On', value: 'on' },
  { label: 'Off', value: 'off' }
];

export const yesNoOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' }
];

export const routeMethodOptions = [
  { label: 'Top', value: 'top' },
  { label: 'Serial', value: 'serial' },
  { label: 'Random', value: 'random' }
];

export const fieldTypeOptions = [
  { label: 'Text', value: 'text' },
  { label: 'Select', value: 'select' },
  { label: 'Date', value: 'date' },
  { label: 'Date and Time', value: 'datetime' },
  { label: 'Textarea', value: 'textarea' },
  { label: 'Number', value: 'number' }
];

export const crmFieldTypeOptions = [
  { label: 'Text', value: 'text' },
  { label: 'Select', value: 'select' },
  { label: 'Date', value: 'date' },
  { label: 'Date and Time', value: 'datetime' },
  { label: 'Textarea', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Button', value: 'button' }
];

export const callModeOptions = [
  { label: 'Inobound', value: 'Inobound' },
  { label: 'Outbound', value: 'Outbound' },
  { label: 'Process', value: 'Process' }
];

export const listSectionOptions = [
  { label: 'Shuffle', value: 'Shuffle' },
  { label: 'Serial', value: 'Serial' }
];

export const scriptOptions = [
  { label: 'Script1', value: '1' },
  { label: 'Script2', value: '2' }
];

export const webformOptions = [
  { label: 'Webform1', value: '1' },
  { label: 'Webform2', value: '2' }
];

export const conferenceOptions = [
  { label: 'External Conference', value: 'external' },
  { label: 'Internal Conference', value: 'internal' },
  { label: 'Hot Key Conference', value: 'hot_key' }
];

export const channelOptions = [
  { label: 1, value: '1' },
  { label: 2, value: '2' },
  { label: 3, value: '3' },
  { label: 4, value: '4' },
  { label: 5, value: '5' },
  { label: 6, value: '6' },
  { label: 7, value: '7' },
  { label: 8, value: '8' },
  { label: 9, value: '9' },
  { label: 10, value: '10' },
  { label: 11, value: '11' },
  { label: 12, value: '12' },
  { label: 13, value: '13' },
  { label: 14, value: '14' },
  { label: 15, value: '15' },
  { label: 16, value: '16' },
  { label: 17, value: '17' },
  { label: 18, value: '18' },
  { label: 19, value: '19' },
  { label: 20, value: '20' }
];

export const intervalTimeOptions = (num = 180) => {
  const result = [];
  for (let i = 0; i <= num; i++) {
    result.push({ label: i, value: i });
  }
  return result;
};

export const attemptsOptions = [
  { label: 1, value: 1 },
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 4, value: 4 },
  { label: 5, value: 5 }
];

export const outboundRoutesOptions = [
  { label: 'outboundroutes1', value: '1' },
  { label: 'outboundroutes2', value: '2' }
];

export const processTypesOptions = [
  { label: 'InComing', options: [{ label: 'InboundAndManual', value: 'InboundAndManual' }] },
  {
    label: 'Only Outgoing',
    options: [
      { label: 'OutboundAndPredictive', value: 'OutboundAndPredictive' },
      { label: 'OutboundAndPreview', value: 'OutboundAndPreview' },
      { label: 'OutboundAndProgressive', value: 'OutboundAndProgressive' }
    ]
  },
  {
    label: 'Blended',
    options: [
      { label: 'BlendedAndPredictive', value: 'BlendedAndPredictive' },
      { label: 'BlendedAndPreview', value: 'BlendedAndPreview' },
      { label: 'BlendedAndProgressive', value: 'BlendedAndProgressive' }
    ]
  }
  // {
  //   label: 'Broadcast',
  //   options: [{ label: 'VoiceBroadcast', value: 'VoiceBroadcast' }]
  // }
];

export const forwardingTypeOptions = [
  { label: 'Transfer To Process', value: 'transfer_to_process' },
  { label: 'Transfer To Queue', value: 'transfer_to_queue' },
  { label: 'Transfer To IVR', value: 'transfer_to_ivr' },
  { label: 'Call Forward (PSTN)', value: 'transfer_to_call_forward' },
  { label: 'Transfer To Agent', value: 'transfer_to_agent' },
  { label: 'Transfer To AI Agent', value: 'transfer_to_ai_number' },
  { label: 'Playback', value: 'transfer_to_playback' },
  { label: 'Hangup', value: 'transfer_to_hangup' }
];

export const daysOfWeek = [
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
  { label: 'Sunday', value: '7' }
];

export const callStatusOptions = [
  { label: 'Completed', value: 'completed' },
  { label: 'Busy', value: 'busy' },
  { label: 'Failed', value: 'failed' },
  { label: 'No Answer', value: 'no-answer' }
];

export const uomOptions = [
  { label: 'Strip', value: 'strip' },
  { label: 'Packet', value: 'packet' },
  { label: 'Bag', value: 'bag' },
  { label: 'Box', value: 'box' },
  { label: 'Standy', value: 'standy' },
  { label: 'Carton', value: 'carton' },
  { label: 'Jar', value: 'jar' },
  { label: 'Pcs', value: 'pcs' }
];

export const performanceDetailsOptions = [
  { label: 'Call Details', value: 'call details' },
  { label: 'Login User', value: 'login user' },
  { label: 'Login Details', value: 'login details' },
  { label: 'Averages', value: 'averages' },
  { label: 'Break', value: 'break' }
];

export const productTypeOptions = [
  { label: 'Namkeen', value: 'namkeen' },
  { label: 'Snack Pellets', value: 'snack_pellets' },
  { label: 'Wafers', value: 'wafers' },
  { label: 'Papad', value: 'papad' },
  { label: 'Flour', value: 'flour' },
  { label: 'Noodles', value: 'noodles' },
  { label: 'Khakhra', value: 'khakhra' },
  { label: 'Bakery', value: 'bakery' },
  { label: 'Sweet & Confectionery', value: 'sweet&confectionery' },
  { label: 'Others', value: 'others' }
];

export const subProductTypeOptions = [
  { label: 'Gathiya', value: 'gathiya' },
  { label: 'Maida', value: 'maida' },
  { label: 'Mixture', value: 'mixture' },
  { label: 'Potato', value: 'potato' },
  { label: 'Murmura', value: 'murmura' },
  { label: 'Sev', value: 'sev' },
  { label: 'Pulse', value: 'pulse' },
  { label: 'Corn', value: 'corn' },
  { label: 'Rice', value: 'rice' },
  { label: 'Snack Pellets', value: 'snack pellets' },
  { label: 'Wafer Potato', value: 'wafer potato' },
  { label: 'Papad', value: 'papad' },
  { label: 'Besan', value: 'besan' },
  { label: 'Maidaa', value: 'maidaa' },
  { label: 'Wheat', value: 'wheat' },
  { label: 'Noodles', value: 'noodles' },
  { label: 'Khakhra', value: 'khakhra' },
  { label: 'Bakery', value: 'bakery' },
  { label: 'Wafer Biscuit', value: 'wafer biscuit' },
  { label: 'Wafer Roll', value: 'wafer roll' },
  { label: 'Chikki', value: 'chikki' },
  { label: 'Confectionery', value: 'confectionery' },
  { label: 'Hanger', value: 'hanger' },
  { label: 'Stand', value: 'stand' }
];

export const playQueueOptions = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '30', value: 30 },
  { label: '60', value: 60 },
  { label: '120', value: 120 },
  { label: '150', value: 150 },
  { label: '180', value: 180 }
];

export const queueDropTimeOptions = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '30', value: 30 },
  { label: '60', value: 60 },
  { label: '120', value: 120 },
  { label: '150', value: 150 },
  { label: '180', value: 180 },
  { label: '240', value: 240 },
  { label: '360', value: 360 }
];

export const maxRetriesOptions = [
  { label: 1, value: '1' },
  { label: 2, value: '2' },
  { label: 3, value: '3' },
  { label: 4, value: '4' },
  { label: 5, value: '5' }
];

export const delayTimeOptions = [
  { label: 4, value: 4 },
  { label: 5, value: 5 },
  { label: 6, value: 6 },
  { label: 7, value: 7 },
  { label: 8, value: 8 },
  { label: 9, value: 9 },
  { label: 10, value: 10 },
  { label: 11, value: 11 },
  { label: 12, value: 12 },
  { label: 13, value: 13 },
  { label: 14, value: 14 },
  { label: 15, value: 15 },
  { label: 16, value: 16 },
  { label: 17, value: 17 },
  { label: 18, value: 18 },
  { label: 19, value: 19 },
  { label: 20, value: 20 }
];

export const zipFileDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const convertToMask = (num) => {
  if (num === null || num === undefined || num === '') {
    return '';
  }
  const numString = num.toString();
  const len = numString.length;

  if (len <= 4) {
    return numString;
  }

  const maskedPart = 'X'.repeat(len - 4);
  const visiblePart = numString.slice(-4);
  return maskedPart + visiblePart;
};

// Report columns rendered as phone numbers. Kept here so the on-screen table and the
// CSV export always mask the exact same set of columns.
export const MASKED_NUMBER_FIELDS = ['crm_number', 'secondaryNumber', 'tertiaryNumber'];

export const getMaskedValue = (value, isMaskingEnabled) =>
  isMaskingEnabled ? convertToMask(value || '-') : value || '-';

export const maskExportRows = (rows, fields = MASKED_NUMBER_FIELDS, isMaskingEnabled = false) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  if (!isMaskingEnabled || !fields?.length) {
    return rows;
  }

  return rows.map((row) => {
    const maskedRow = { ...row };
    fields.forEach((field) => {
      if (maskedRow[field]) {
        maskedRow[field] = convertToMask(maskedRow[field]);
      }
    });
    return maskedRow;
  });
};

export const dtmfOptionsInitialValue = [
  {
    dtmf: 1,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 2,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 3,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 4,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 5,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 6,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 7,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 8,
    forwarding_type: '',
    forwarding_option: ''
  },
  {
    dtmf: 9,
    forwarding_type: '',
    forwarding_option: ''
  }
];

// Dynamic IVRs support the full 0-9 keypad.
// NOTE: dtmfOptionsInitialValue above stays 1-9 on purpose - the normal IVR
// screen uses it and adding 0 there is out of scope.
export const dynamicIvrDtmfOptionsInitialValue = Array.from({ length: 10 }, (_, dtmf) => ({
  dtmf,
  forwarding_type: '',
  forwarding_option: ''
}));

export const languageSelectOptions = [
  { label: 'English', value: 'en' },
  { label: 'Hindi', value: 'hi' }
];

export const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'हिंदी', value: 'hi' },
  { label: 'ગુજરાતી', value: 'gu' }
];

export const validateCSVPhoneNumbers = (file) => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      complete: (result) => {
        const rows = result.data;
        const phoneNumberColumnIndex = 0;

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const phoneNumber = row[phoneNumberColumnIndex];

          if (phoneNumber && (phoneNumber.length !== 10 || isNaN(phoneNumber))) {
            resolve(false);
            return;
          }
        }
        resolve(true);
      },
      error: (error) => {
        console.error('Error parsing CSV', error);
        resolve(false);
      },
      header: false,
      skipEmptyLines: true
    });
  });
};

export const checkModulePermission = (permission_type, module_name, userRolePermission) => {
  const getDetails = userRolePermission?.find((x) => x?.module_name === module_name);
  return getDetails ? getDetails?.permission?.[permission_type] : false;
};

export function getFileExtension(filePath) {
  if (!filePath || typeof filePath !== 'string') return null;

  const parts = filePath.split('.');
  if (parts.length <= 1) return null;

  return parts.pop().toLowerCase();
}

export const monthNames = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
];

export const getDateRangeFromLabel = (label, timezone = 'GMT') => {
  if (!label || typeof label !== 'string' || !label.includes('-')) {
    return { minDate: null, maxDate: null };
  }

  const [monthStr, yearStr] = label.split('-');
  const monthIndex = monthNames.indexOf(monthStr.toLowerCase());

  if (monthIndex === -1 || isNaN(yearStr)) {
    return { minDate: null, maxDate: null };
  }

  const year = parseInt(yearStr);

  const minDate = new Date(year, monthIndex, 1, 0, 0, 1);

  const now = toZonedTime(new Date(), timezone);
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() === monthIndex;

  const maxDate = isCurrentMonth
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
    : endOfMonth(minDate);

  return { minDate, maxDate };
};

export const formatArchiveOptions = (data, timezone = 'GMT') => {
  const currentDate = toZonedTime(new Date(), timezone);
  const currentMonth = currentDate.toLocaleString('default', { month: 'long' }); // e.g., "June"
  const currentMonthCapitalized =
    currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1).toLowerCase();
  const currentYear = currentDate.getFullYear();

  const monthOrder = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];

  // Sort by year descending, then by month descending
  const sortedData = [...data].sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year; // Descending year
    }
    return (
      monthOrder.indexOf(b.month.toLowerCase()) - monthOrder.indexOf(a.month.toLowerCase()) // Descending month
    );
  });

  // Format the sorted data
  const formattedData = sortedData.map((val) => {
    const monthCapitalized = val.month.charAt(0).toUpperCase() + val.month.slice(1).toLowerCase();
    return {
      label: `${monthCapitalized}-${val.year}`,
      value: val.id
    };
  });

  // Return with current month/year on top
  return [
    {
      label: `${currentMonthCapitalized}-${currentYear}`,
      value: 0
    },
    ...formattedData
  ];
};

export const cleanPhoneNumber = (input) => {
  if (typeof input !== 'string') return '';

  if (input.endsWith('@c.us')) {
    let cleaned = input.replace('@c.us', '');
    if (cleaned.startsWith('91')) {
      cleaned = cleaned.slice(2);
    }
    return cleaned;
  }

  return input;
};

export const formatPayloadDate = (val_date) => {
  const inputDate = new Date(val_date);
  return format(inputDate, 'yyyy-MM-dd HH:mm:ss');
};

export const formatPayloadTime = (val_date) => {
  const inputDate = new Date(val_date);
  return format(inputDate, 'HH:mm');
};

export const formatDownloadDate = (val_date) => {
  const inputDate = new Date(val_date);
  return format(inputDate, 'yyyy-MM-dd HH:mm:ss');
};

// Table display helper for API timestamps that come back as 'yyyy-MM-dd HH:mm:ss'.
// Parsed explicitly instead of via new Date(), which treats that space separated
// format as non standard, then falls back for ISO values. Never throws on bad input.
export const formatTableDateTime = (val_date, dateFormat = 'yyyy-MM-dd HH:mm:ss') => {
  if (!val_date) return '-';
  if (val_date instanceof Date) {
    return isValid(val_date) ? format(val_date, dateFormat) : '-';
  }
  const parsedDate = parse(String(val_date), 'yyyy-MM-dd HH:mm:ss', new Date());
  const inputDate = isValid(parsedDate) ? parsedDate : new Date(val_date);
  return isValid(inputDate) ? format(inputDate, dateFormat) : '-';
};

export const minutesToSeconds = (minutes) => {
  const value = Number(minutes);
  if (isNaN(value) || value < 0) return null;
  return Math.round(value * 60);
};

export const secondsToMinutes = (seconds) => {
  const value = Number(seconds);
  if (isNaN(value) || value < 0) return null;
  const minutes = value / 60;
  return Number(minutes.toFixed(2));
};

export const formatDuration = (duration) => {
  if (!duration) return '0 seconds';

  const [minPart, secPart] = duration.split(':');

  const minutes = parseInt(minPart.split('.')[0], 10) || 0;
  const seconds = parseInt(secPart.split('.')[0], 10) || 0;

  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};
